/* eslint-disable import/no-unused-modules */
import type {
    Currency,
    InvoicePaymentStatus,
    Proposal,
} from '../../type/types';
import {
    QUOTE_COLUMNS,
    INVOICE_COLUMNS,
    PDF_QUOTE_COLUMNS,
    PDF_INVOICE_COLUMNS,
} from '../../constants';
import { Button, cn } from '@rocco/index';
import {
    formatDate,
    formatPrice,
    getCountryByCurrency,
} from '../../util/utils';
import { CheckLine } from '@archipro-design/icons';
import type { PaymentItem } from '../../type/types';
import { PROPOSAL_TYPO_CLASS } from './proposal-styles';

interface PaymentRowProps {
    proposal: Proposal;
    paymentItem: PaymentItem;
    currency: Currency;
    isMobile: boolean;
    isPdf?: boolean;
    onPayNowClick: (paymentId: number) => void;
    enablePayButton: boolean;
}

const getStatusName = (status: InvoicePaymentStatus) => {
    let statusName = '';
    switch (status) {
        case 'FullyPaid':
            statusName = 'Paid';
            break;
        case 'Upcoming':
            statusName = 'Unpaid';
            break;
        default:
            statusName = status;
    }
    // capitalized
    return statusName.charAt(0).toUpperCase() + statusName.slice(1);
};

const STATUS_BADGE_STYLES: Record<
    InvoicePaymentStatus,
    { bg: string; text: string }
> = {
    FullyPaid: { bg: '#B1CEB3', text: '#FFFFFF' },
    Refunded: { bg: '#D0D0D0', text: '#FFFFFF' },
    Overdue: { bg: '#FFB4B4', text: '#943E3E' },
    Upcoming: { bg: '#D0D0D0', text: '#FFFFFF' },
    Canceled: { bg: '#D0D0D0', text: '#FFFFFF' },
};

const StatusBadge = ({ status }: { status: InvoicePaymentStatus }) => {
    const { bg, text } = STATUS_BADGE_STYLES[status];
    return (
        <Button
            size="narrow"
            textSize="small"
            className="flex h-[1.375rem] w-[5rem] cursor-default items-center justify-center border-none"
            style={{ backgroundColor: bg }}
        >
            <span
                className={cn(
                    PROPOSAL_TYPO_CLASS.PAYMENT_TABLE_ROW_STATUS,
                    'leading-none',
                )}
                style={{ color: text }}
            >
                {getStatusName(status)}
            </span>
        </Button>
    );
};

const InvoicePayButton = ({
    enablePayButton,
    status,
    onClick,
}: {
    status: PaymentItem['status'];
    enablePayButton: boolean;
    onClick: () => void;
}) => {
    const buttonClass = 'h-[2rem] w-[5.9rem] border-none  md:h-11 print:h-11';
    if (status === 'FullyPaid') {
        return (
            <Button className={cn(buttonClass, 'bg-[#D0D0D0]')}>
                <span
                    className={cn(
                        PROPOSAL_TYPO_CLASS.PAYMENT_TABLE_ROW_PAY_BUTTON_PAID,
                        'ml-1 mt-[0.2rem]',
                    )}
                >
                    Paid
                    <CheckLine className="mb-1 ml-2 h-6 w-6 fill-white" />
                </span>
            </Button>
        );
    }
    return (
        <Button
            color="primary-bm"
            className={cn(buttonClass)}
            onClick={onClick}
            disabled={!enablePayButton}
        >
            <span
                className={PROPOSAL_TYPO_CLASS.PAYMENT_TABLE_ROW_PAY_BUTTON_PAY}
            >
                Pay
            </span>
        </Button>
    );
};

/**
 * Payment row component for displaying payment information in a grid layout
 */
export const PaymentRow = ({
    proposal,
    paymentItem,
    currency,
    isMobile,
    isPdf = false,
    onPayNowClick,
    enablePayButton,
}: PaymentRowProps) => {
    const docType = proposal.docType;
    const country = getCountryByCurrency(currency);
    if (isMobile) {
        return (
            <div className="rounded-md bg-[#f6f6f6] px-[0.55rem] py-[0.98rem]">
                {/* First row: Payment name */}
                <div
                    className={cn(
                        PROPOSAL_TYPO_CLASS.PAYMENT_TABLE_ROW_TITLE,
                        'mb-3',
                    )}
                >
                    {paymentItem.name}
                </div>

                {/* Second row: Status badge, due date, and price */}
                <div className="flex items-center gap-2">
                    {/* Status badge - flexible width */}
                    <div className="w-[38%] flex-shrink-0">
                        {docType === 'quote' ? (
                            <StatusBadge status={paymentItem.status} />
                        ) : (
                            <InvoicePayButton
                                status={paymentItem.status}
                                enablePayButton={enablePayButton}
                                onClick={() => {
                                    onPayNowClick(paymentItem.id);
                                }}
                            />
                        )}
                    </div>

                    {/* Due date - flexible width */}
                    <div className="flex w-[30%] flex-col">
                        <span
                            className={cn(
                                PROPOSAL_TYPO_CLASS.PAYMENT_TABLE_HEADER,
                                'mb-1 uppercase',
                            )}
                        >
                            Due
                        </span>
                        <span className={PROPOSAL_TYPO_CLASS.PAYMENT_TABLE_ROW}>
                            {formatDate(paymentItem.dueDate, country)}
                        </span>
                    </div>

                    {/* Price - flexible width */}
                    <div className="flex w-[32%] flex-col text-right">
                        <span
                            className={cn(
                                PROPOSAL_TYPO_CLASS.PAYMENT_TABLE_HEADER,
                                'mb-1 uppercase',
                            )}
                        >
                            Price
                        </span>
                        <span className={PROPOSAL_TYPO_CLASS.PAYMENT_TABLE_ROW}>
                            {formatPrice(paymentItem.amount, currency)}
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    const isQuote = docType === 'quote';
    const columns = isPdf
        ? isQuote
            ? PDF_QUOTE_COLUMNS
            : PDF_INVOICE_COLUMNS
        : isQuote
          ? QUOTE_COLUMNS
          : INVOICE_COLUMNS;

    return (
        <div className="flex min-h-[3.5rem] items-center rounded-md bg-[#f6f6f6] backdrop-blur-[30px]">
            {columns.map((col) => {
                switch (col.key) {
                    case 'title':
                        return (
                            <div
                                key={col.key}
                                className={cn(
                                    PROPOSAL_TYPO_CLASS.PAYMENT_TABLE_ROW_TITLE,
                                    col.className,
                                    'md:ml-6 print:ml-6',
                                )}
                            >
                                {paymentItem.name}
                            </div>
                        );
                    case 'status':
                        return (
                            <div
                                key={col.key}
                                className={cn('flex', col.className)}
                            >
                                <StatusBadge status={paymentItem.status} />
                            </div>
                        );
                    case 'due':
                        return (
                            <div
                                key={col.key}
                                className={cn(
                                    PROPOSAL_TYPO_CLASS.PAYMENT_TABLE_ROW,
                                    col.className,
                                    'whitespace-nowrap print:text-base',
                                )}
                            >
                                {formatDate(paymentItem.dueDate, country)}
                            </div>
                        );
                    case 'paidOn':
                        return (
                            <div
                                key={col.key}
                                className={cn(
                                    PROPOSAL_TYPO_CLASS.PAYMENT_TABLE_ROW,
                                    col.className,
                                    'whitespace-nowrap print:text-base',
                                )}
                            >
                                {formatDate(paymentItem.paymentDate, country)}
                            </div>
                        );
                    case 'amount':
                        return (
                            <div
                                key={col.key}
                                className={cn(
                                    PROPOSAL_TYPO_CLASS.PAYMENT_TABLE_ROW,
                                    col.className,
                                    'whitespace-nowrap print:text-base',
                                )}
                            >
                                {formatPrice(paymentItem.amount, currency)}
                            </div>
                        );
                    case 'payButton':
                        return (
                            <div key={col.key} className={col.className}>
                                <InvoicePayButton
                                    status={paymentItem.status}
                                    enablePayButton={enablePayButton}
                                    onClick={() => {
                                        onPayNowClick(paymentItem.id);
                                    }}
                                />
                            </div>
                        );
                    default:
                        return null;
                }
            })}
        </div>
    );
};

export default PaymentRow;
